import React, { useEffect } from "react";

import { RouterProvider } from "react-router-dom";
import { Provider as ReduxProvider, useSelector } from "react-redux";

import "./App.css";

import "bootstrap/dist/css/bootstrap.min.css";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "tippy.js/dist/tippy.css";

import store from "./redux/reduxStore";
import { removeAuth } from "./redux/actions/auth/authActions";

import router from "./core/routes";

import GlobalLoader from "./components/global/loading";

import { fetchAccessToken } from "./api/auth";
import { Helmet } from "react-helmet";

import io from "socket.io-client";
import {
  clearSyncToastNotifications,
  getSyncToastNotifications,
} from "./utils/storage";
import { receiveNotificationEvent } from "./events/notification";
import { messageSeenEvent, receiveMessageEvent } from "./events/message";

function App() {
  useEffect(() => {
    const isLogged = localStorage.getItem("logged");

    if (isLogged) {
      handleAuthentication();
    }

    async function handleAuthentication() {
      try {
        await fetchAccessToken();
      } catch (error) {
        const message =
          error?.response?.data?.error?.message ||
          "You have been logged out. Please login again.";

        toast.error(message);

        store.dispatch(removeAuth({ message }));
      }
    }

    const syncNotifications = getSyncToastNotifications();

    if (syncNotifications.length) {
      syncNotifications.forEach((notification) => {
        const type = notification.type;

        switch (type) {
          case "ERROR":
            toast.error(notification.message);
            break;
          case "SUCCESS":
            toast.success(notification.message);
            break;
          case "INFO":
            toast.info(notification.message);
            break;
          case "WARNING":
            toast.warning(notification.message);
            break;
          default:
            toast(notification.message);
        }
      });

      clearSyncToastNotifications();
    }
  }, []);

  // useEffect(()=>{
  //   // requestIdleCallback have to use this and check working

  //   if ('requestIdleCallback' in window) {
  //     window.requestIdleCallback(function () {
  //       loadPages.listing()
  //     }
  //     )
  //   }
  //   else {
  //     console.log('requestIdleCallback not supported');
  //   }

  // },[])

  const mapsApiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  return (
    <>
      <Helmet>
        <script
          src={`https://maps.googleapis.com/maps/api/js?key=${mapsApiKey}&libraries=places`}
          async
          defer
        ></script>
      </Helmet>
      <ReduxProvider store={store}>
        <RealTimeEvents />

        <React.Suspense fallback={null}>
          <RouterProvider router={router} />
        </React.Suspense>

        <GlobalLoader />

        <ToastContainer autoClose={5000} />
      </ReduxProvider>
    </>
  );
}

export default App;

function RealTimeEvents() {
  const { notificationsToken } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!notificationsToken) {
      return;
    }

    let socket;

    try {
      socket = io(process.env.REACT_APP_SERVER_URL_NOTIFICATIONS, {
        auth: {
          token: notificationsToken,
        },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
      });

      socket.on("connect", () => {
        console.log("Connected to the notification server.");
      });

      socket.on("guest-notification", (data) => {
        receiveNotificationEvent(data.details);
      });

      socket.on("guest-message", (data) => {
        receiveMessageEvent(data.details);
      });

      socket.on("partner-message-seen-specific-event", (data) => {
        messageSeenEvent(data.details);
      });

      socket.on("partner-message-seen-all-event", (data) => {
        messageSeenEvent(data.details);
      });

      socket.on("connect_error", (error) => {
        console.error("Connection error:", error);
      });

      socket.on("disconnect", (reason) => {
        console.warn(`Disconnected from server: ${reason}`);
      });
    } catch (error) {
      console.error("Failed to initialize socket:", error);
    }

    return () => {
      if (socket) {
        socket.off("guest-notification");
        socket.off("guest-message");
        socket.off("connect");
        socket.off("disconnect");
        socket.off("connect_error");
        socket.disconnect();
      }
    };
  }, [notificationsToken]);

  return null;
}
